import { Spacing } from '@persona-id/design-tokens';
import { LoaderIcon } from '@persona-id/design-tokens/icons';
import { isEmpty, isFunction } from 'lodash/fp';
import {
  ComponentPropsWithoutRef,
  forwardRef,
  MouseEvent,
  ReactNode,
  Ref,
  useCallback,
} from 'react';
import { Link, LinkProps } from 'react-router-dom';
import styled, { css, DefaultTheme, FlattenInterpolation, ThemeProps } from 'styled-components';

import { printMixins } from 'dashboard/lib/printMixins';
import hoverableMixin from 'design-system/mixins/hoverable';
import { paragraphMixins } from 'design-system/mixins/text';
import PseudoClassProps from 'design-system/types/PseudoClassProps';
import { rotate } from 'design-system/variables/keyframes';
import { TransitionDuration } from 'design-system/variables/transitions';
import useToggle from 'lib/hooks/useToggle';

const noop = (): void => undefined;

// TODO(joseph): Bring this fix back to pseudoClass
const isActive = ({ active }: PseudoClassProps) => (active ? '' : ':active:enabled');

/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
const sizeStyles: Record<ButtonProps['size'], FlattenInterpolation<ThemeProps<DefaultTheme>>> = {
  md: css`
    ${paragraphMixins.md}
    height: ${({ theme }) => theme.button.height.md};
    padding: ${Spacing[2]};
    svg {
      height: 16px;
      width: 16px;
    }
  `,
  sm: css`
    ${paragraphMixins.sm}
    height: ${({ theme }) => theme.button.height.sm};
    padding: ${Spacing[1]};
    svg {
      height: 16px;
      width: 16px;
    }
  `,
};

/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
const widthStyles: Record<ButtonProps['width'], FlattenInterpolation<ThemeProps<DefaultTheme>>> = {
  full: css`
    width: 100%;
  `,
  auto: css`
    width: auto;
  `,
  // TODO: rename this prop to 'element' as a standard unresponsive element width
  button: css`
    width: ${({ theme }) => theme.element.width.md};
  `,
  icon: css<ButtonProps>`
    width: ${({ theme, size }) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      return theme.button.height[size];
    }};
    padding: 0;
    flex: none;
  `,
};

interface ButtonPropsWithTransientProps extends Omit<ButtonProps, 'isPending'> {
  $isPending: boolean;
}

export const StyledButton = styled.button.attrs<ButtonPropsWithTransientProps>(({ to }) => {
  if (isEmpty(to)) {
    return 'button';
  } else if (typeof to === 'string' && to.startsWith('https://')) {
    return {
      as: 'a',
      href: to,
      target: '_blank',
      rel: 'noreferrer',
    };
  } else {
    return {
      as: Link,
    };
  }
})<ButtonPropsWithTransientProps>`
  ${hoverableMixin};

  border: none;
  box-sizing: border-box;
  outline: 0 !important;
  white-space: nowrap;
  ${({ theme, variant }) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const palette = theme.color.variants[variant] as any;
    return css`
      &:disabled {
        background-color: ${palette.disabled};
        color: ${palette.textDisabled};
        svg path {
          stroke: ${palette.textDisabled};
        }

        /* For backwards compatibility with current coloring scheme (targeting stroke property) */
        svg.pds-icon {
          path {
            stroke: unset;
          }

          /* Support icons with stroke */
          path[stroke] {
            stroke: currentColor;
          }
        }

        user-select: none;
      }
    `;
  }}

  display: flex;
  align-items: center;
  justify-content: center;

  /* To properly position loading spinner */
  position: relative;

  border-radius: ${({ theme }) => theme.button.borderRadius};

  transition: ${TransitionDuration.Fast};

  ${({ size }) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return sizeStyles[size];
  }}
  ${({ width }) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return widthStyles[width];
  }}

  /* Variant styling */
  ${({ theme, variant, $isPending }) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const palette = theme.color.variants[variant] as any;
    return css`
      background-color: ${palette.default};
      color: ${palette.text};
      svg path {
        stroke: ${palette.text};
      }

      svg.pds-icon {
        path {
          stroke: unset;
        }

        path[stroke] {
          stroke: currentColor;
        }
      }

      &${isActive} {
        background-color: ${palette.active};
      }

      ${$isPending &&
      css`
        cursor: default;
        background-color: ${palette.active};
        &:disabled {
          background-color: ${({ theme }) => {
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            return theme.color.variants[variant].active;
          }};
          user-select: none;
        }

        svg path {
          stroke: ${palette.highlightDark};
        }

        // For backwards compatibility with current coloring scheme (targeting stroke property)
        svg.pds-icon {
          path {
            stroke: unset;
          }

          path[stroke] {
            stroke: currentColor;
          }
        }
      `}
    `;
  }}


  ${printMixins.hide}
`;

type WithPendingProps = Pick<ButtonProps, 'isPending'>;

const LoaderContainer = styled.div<WithPendingProps>`
  position: absolute;
  height: 100%;
  width: 100%;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 2s;
  opacity: ${({ isPending }) => (isPending ? '1' : '0')};
`;

const Loader = styled(LoaderIcon)`
  animation: ${rotate} linear 1s infinite;
`;

const ButtonChildren = styled('div').attrs(({ isPending }: WithPendingProps) => ({
  ['aria-hidden']: isPending,
}))<WithPendingProps>`
  width: 100%;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: center;
  transition: opacity ${TransitionDuration.Fast};
  opacity: ${({ isPending }) => (isPending ? '0' : '1')};
`;

const PrefixIconContainer = styled.div`
  display: flex;
  align-items: center;
  margin-right: ${Spacing[1]};
`;

const InheritPrefixIconContainer = styled.div`
  display: flex;
  align-items: center;

  & svg {
    width: inherit;
    height: inherit;
  }
`;

export const SuffixIconContainer = styled(PrefixIconContainer)`
  margin-left: ${Spacing[1]};
  margin-right: 0;
  flex-grow: 1;
  justify-content: flex-end;
`;

type MaybePromise =
  | ((e: MouseEvent<HTMLButtonElement>) => void)
  | ((e: MouseEvent<HTMLButtonElement>) => Promise<void>);

export interface ButtonProps
  extends ComponentPropsWithoutRef<'button'>,
    Omit<PseudoClassProps, 'checked'> {
  variant?: 'basic' | 'primary' | 'inverse' | 'danger';
  /**
   * Loading state of icon
   */
  isPending?: boolean;
  /**
   * Prepended icon element
   */
  prefixIcon?: ReactNode;
  prefixIconType?: 'basic' | 'inherit';

  /**
   * Appended icon element
   */
  suffixIcon?: ReactNode;

  size?: 'md' | 'sm';

  /**
   * Changes the button into a link
   */
  to?: LinkProps['to'];

  /**
   * Note: we may want to default this to 'button' in the future, but many instances of this Button component
   * in dashboard currently rely on the default 'submit' behavior that occurs when using this in redux-form <form/>,
   * so we'll have to comb through and manually add the type='submit' prop.
   */
  type?: 'button' | 'reset' | 'submit';
  width?: 'full' | 'auto' | 'button' | 'icon';
  onClick?: MaybePromise;
  ref?: Ref<HTMLButtonElement>;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    id,
    children,
    prefixIcon,
    suffixIcon,
    disabled,
    isPending,
    type = 'button',
    prefixIconType = 'basic',
    variant = 'primary',
    size = 'md',
    width = 'full',
    onClick,
    ...props
  }: ButtonProps,
  ref,
) {
  const [isResolving, { enable, disable }] = useToggle(false);
  const innerOnClick: MaybePromise = isFunction(onClick) ? onClick : noop;

  const loading = isPending || isResolving;

  const handleClick = useCallback(
    async (e: MouseEvent<HTMLButtonElement>) => {
      if (loading) {
        return;
      }

      const maybePromise = innerOnClick(e);

      if (maybePromise instanceof Promise) {
        enable();
        await maybePromise;
        disable();
      }
    },
    [loading, innerOnClick],
  );

  return (
    <StyledButton
      id={id}
      type={type}
      data-test={'button__' + variant}
      variant={variant}
      size={size}
      width={width}
      disabled={disabled || loading}
      $isPending={loading}
      {...props}
      onClick={handleClick}
      ref={ref}
    >
      {loading && (
        <LoaderContainer isPending={loading}>
          <Loader aria-label={`Loading ${props['aria-label'] || ''}`} />
        </LoaderContainer>
      )}
      <ButtonChildren isPending={loading}>
        {prefixIcon != null && (
          <>
            {prefixIconType === 'basic' && <PrefixIconContainer>{prefixIcon}</PrefixIconContainer>}
            {prefixIconType === 'inherit' && (
              <InheritPrefixIconContainer>{prefixIcon}</InheritPrefixIconContainer>
            )}
          </>
        )}
        {children}
        {suffixIcon != null && <SuffixIconContainer>{suffixIcon}</SuffixIconContainer>}
      </ButtonChildren>
    </StyledButton>
  );
});

export default Button;
